import React, { useEffect } from "react";
import type { BikeWithLocation } from "../../types/bike";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";
import {
  formatTimeAgo,
  formatSpeed,
  formatBattery,
  getStatusBadgeColor,
  getBatteryColor,
} from "../../utils/formatters";
import { MapPin, Clock, Zap, Battery } from "lucide-react";

interface BikeListProps {
  bikes: BikeWithLocation[];
  loading?: boolean;
  onBikeClick?: (bikeId: string) => void;
}

export const BikeList: React.FC<BikeListProps> = ({
  bikes,
  loading = false,
  onBikeClick,
}) => {
  const { loadIfNeeded: loadCategoriesIfNeeded, getCategoryById } =
    useVehicleCategoryStore();

  useEffect(() => {
    loadCategoriesIfNeeded().catch(console.error);
  }, [loadCategoriesIfNeeded]);

  if (loading && !bikes.length) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (!bikes.length) {
    return <div className="text-center p-8 text-gray-500">No bikes found</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Bike
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Category
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Location
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Speed
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Battery
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Seen
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {bikes.map((bike) => (
            <tr
              key={bike.bike_id}
              className="hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onBikeClick?.(bike.bike_id)}
            >
              <td className="px-6 py-3 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {bike.bike_id}
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {getCategoryById(bike.category_id)}
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(
                    bike.status,
                  )}`}
                >
                  {bike.status}
                </span>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                {bike.last_location ? (
                  <a
                    href={`https://www.google.com/maps/search/${bike.last_location.latitude},${bike.last_location.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>
                      {bike.last_location.latitude.toFixed(4)},{" "}
                      {bike.last_location.longitude.toFixed(4)}
                    </span>
                  </a>
                ) : (
                  <span className="text-gray-400">No location</span>
                )}
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-1">
                  <Zap className="w-4 h-4 text-gray-400" />
                  <span>{formatSpeed(bike.last_location?.speed)}</span>
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm">
                <div className="flex items-center space-x-1">
                  <Battery
                    className={`w-4 h-4 ${getBatteryColor(
                      bike.last_location?.battery_level,
                    )}`}
                  />
                  <span
                    className={getBatteryColor(
                      bike.last_location?.battery_level,
                    )}
                  >
                    {formatBattery(bike.last_location?.battery_level)}
                  </span>
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                {bike.last_location?.timestamp ? (
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span
                      title={new Date(
                        bike.last_location.timestamp,
                      ).toLocaleString()}
                    >
                      {formatTimeAgo(bike.last_location.timestamp)}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-400">Never</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
